import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowRight, Sparkles, Star, ShieldCheck, Clock, IndianRupee, Zap, Search,
  Wrench, Scissors, ShoppingBasket, Pill, Heart, GraduationCap,
  Sprout, Car, Hammer, BrickWall, Bike, Truck, Wheat, PawPrint,
  HeartHandshake, ChefHat, MessageSquare,
} from "lucide-react";
import { unitSuffix } from "@/lib/pricing";
import RequestSection from "@/components/app/RequestSection";
import FeedbackSection from "@/components/app/FeedbackSection";
import PublicInsightsSection from "@/components/app/PublicInsightsSection";

const ICONS = {
  sparkles: Sparkles, wrench: Wrench, zap: Zap, scissors: Scissors,
  "shopping-basket": ShoppingBasket, pill: Pill, heart: Heart, "graduation-cap": GraduationCap,
  sprout: Sprout, car: Car, hammer: Hammer, "brick-wall": BrickWall,
  bike: Bike, truck: Truck, wheat: Wheat, "paw-print": PawPrint,
  "heart-handshake": HeartHandshake, "chef-hat": ChefHat,
};

export default function Landing() {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/services").then(({ data }) => setServices(data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div className="space-y-16" data-testid="landing-page">
      {/* Hero — serene ocean coast */}
      <section className="relative overflow-hidden rounded-3xl border border-white/10">
        {/* Ocean background */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1505142468610-359e7d316be0?auto=format&fit=crop&w=1920&q=80"
            alt="Serene wild ocean coast with turquoise blue water"
            className="h-full w-full object-cover"
          />
          {/* Layered gradients for readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#012036]/80 via-[#02466f]/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#01162a]/60 via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 grid gap-8 p-6 sm:p-10 lg:grid-cols-5 lg:gap-8 lg:p-14">
          <div className="flex flex-col justify-center text-white lg:col-span-3">
            <div className="mb-5 inline-flex w-fit items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-white backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-orange animate-pulse" />
              Local services, delivered like ocean tides
            </div>
            <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Book trusted <span className="text-brand-orange">local pros</span>.
              <br />
              Pay by UPI.{" "}
              <span className="underline decoration-brand-cyan decoration-[6px] underline-offset-[10px]">Done.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/85">
              From salon-at-home to plumbing, groceries and even matrimonial help —
              TridentDigitalPulse verifies every provider and confirms your booking
              the moment your UPI payment succeeds. Calm as the coast, sharp as the tide.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/browse"
                className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-black/30 transition-transform hover:-translate-y-0.5"
                data-testid="hero-cta-browse"
              >
                Explore services <ArrowRight size={16} />
              </Link>
              <Link
                to="/register?role=provider"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-6 py-3 text-sm font-semibold text-white backdrop-blur transition-transform hover:-translate-y-0.5 hover:bg-white/15"
                data-testid="hero-cta-provider"
              >
                Become a provider
              </Link>
              <a
                href="#feedback"
                className="inline-flex items-center gap-2 rounded-full border border-brand-yellow/60 bg-brand-yellow/20 px-6 py-3 text-sm font-semibold text-brand-yellow backdrop-blur transition-transform hover:-translate-y-0.5 hover:bg-brand-yellow/30"
                data-testid="hero-cta-feedback"
              >
                <MessageSquare size={16} /> Share feedback
              </a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/85">
              <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-brand-cyan" /> KYC-verified pros</div>
              <div className="flex items-center gap-2"><Clock size={16} className="text-brand-cyan" /> Same-day slots</div>
              <div className="flex items-center gap-2"><IndianRupee size={16} className="text-brand-cyan" /> UPI auto-confirm</div>
            </div>
          </div>

          {/* Floating glass cards */}
          <div className="relative flex min-h-[320px] items-center justify-center py-8 lg:col-span-2">
            <div className="relative w-full max-w-sm">
              <div className="animate-fade-up rounded-3xl border border-white/25 bg-white/15 p-5 text-white shadow-2xl backdrop-blur-xl">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/85">
                  <Star size={12} className="fill-brand-yellow text-brand-yellow" /> 4.9 · 12k+ bookings
                </div>
                <div className="mt-3 font-heading text-xl font-extrabold leading-tight sm:text-2xl">
                  Deep clean today at 3:00 PM
                </div>
                <div className="mt-1 text-xs text-white/70">HSR Layout · Ravi Kumar · ⭐ 4.8</div>
                <div className="mt-4 flex items-center justify-between rounded-xl bg-white/10 p-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-white/60">Paid via UPI</div>
                    <div className="font-heading text-lg font-extrabold">₹1,499</div>
                  </div>
                  <div className="rounded-full bg-brand-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                    Confirmed ✓
                  </div>
                </div>
              </div>
              <div
                className="absolute -bottom-8 -right-2 hidden animate-fade-up rounded-2xl border border-white/25 bg-white/20 p-3 text-white shadow-xl backdrop-blur-xl sm:block"
                style={{ animationDelay: "150ms" }}
              >
                <div className="text-[10px] font-semibold uppercase tracking-widest text-white/70">Live now</div>
                <div className="font-heading text-sm font-bold">37 pros online</div>
              </div>
              <div
                className="absolute -left-4 -top-10 hidden animate-fade-up rounded-2xl border border-white/25 bg-white/20 p-3 text-white shadow-xl backdrop-blur-xl md:block"
                style={{ animationDelay: "300ms" }}
              >
                <div className="text-[10px] font-semibold uppercase tracking-widest text-white/70">Avg confirm</div>
                <div className="font-heading text-sm font-bold">⚡ 12 seconds</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section>
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              What do you need today?
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">Curated pros across everyday categories.</p>
          </div>
          <Link to="/browse" className="hidden text-sm font-semibold text-brand-orange sm:inline-flex" data-testid="see-all-categories">
            See all →
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4" data-testid="category-grid">
          {categories.map((c, i) => {
            const Icon = ICONS[c.icon] || Sparkles;
            return (
              <Link
                key={c.id}
                to={`/browse?category=${c.slug}`}
                data-testid={`category-tile-${c.slug}`}
                className="group relative overflow-hidden rounded-2xl border border-foreground/10 bg-white"
                style={{ animationDelay: `${i * 50}ms` }}
              >
                <div className="aspect-[5/4] overflow-hidden">
                  <img src={c.image_url} alt={c.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="flex items-center justify-between p-3">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Category</div>
                    <div className="font-heading text-sm font-bold sm:text-base">{c.name}</div>
                  </div>
                  <span className="grid h-8 w-8 place-items-center rounded-full text-white" style={{ backgroundColor: c.color || "#FF3B30" }}>
                    <Icon size={14} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured providers */}
      <section>
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">Top-rated near you</h2>
            <p className="mt-2 text-sm text-muted-foreground">Verified pros ready to help this week.</p>
          </div>
          <Link to="/browse" className="text-sm font-semibold text-brand-orange">Browse all →</Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="featured-services">
          {services.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground">
              No services yet — check back soon.
            </div>
          )}
          {services.map((s) => (
            <ServiceCard key={s.id} service={s} />
          ))}
        </div>
      </section>

      {/* Feedback capture — HIGHEST PRIORITY on the landing page */}
      <FeedbackSection />

      {/* Public market-voice insights — social proof from real, anonymised data */}
      <PublicInsightsSection />

      {/* Outside product / service request board */}
      <RequestSection />

      {/* How it works */}
      <section className="rounded-3xl bg-brand-ink px-6 py-12 text-white sm:px-10 lg:px-14">
        <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">
          Book in <span className="text-brand-cyan">3 taps</span>, get help in hours.
        </h2>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {[
            { icon: Search, title: "Discover", body: "Pick a category, compare pros, read genuine reviews." },
            { icon: IndianRupee, title: "Pay by UPI", body: "Scan a QR or use the merchant UPI ID — instantly." },
            { icon: ShieldCheck, title: "Auto-confirmed", body: "Booking is locked the moment your payment succeeds." },
          ].map((step, i) => (
            <div key={step.title} className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <div className="mb-3 grid h-10 w-10 place-items-center rounded-xl bg-brand-orange">
                <step.icon size={18} />
              </div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-white/60">Step {i + 1}</div>
              <div className="mt-1 font-heading text-lg font-bold">{step.title}</div>
              <p className="mt-1 text-sm leading-relaxed text-white/70">{step.body}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function ServiceCard({ service }) {
  return (
    <Link
      to={`/services/${service.id}`}
      data-testid={`service-card-${service.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-foreground/10 bg-white transition-transform hover:-translate-y-1 hover:shadow-lg"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img src={service.image_url || service.category?.image_url} alt={service.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-brand-orange">{service.category?.name}</div>
          <div className="flex items-center gap-1 text-xs font-semibold">
            <Star size={12} className="fill-brand-yellow text-brand-yellow" />
            {service.provider?.rating_avg || "New"}
          </div>
        </div>
        <div className="font-heading text-base font-bold leading-tight">{service.title}</div>
        <div className="line-clamp-2 text-xs text-muted-foreground">{service.description}</div>
        <div className="mt-auto flex items-center justify-between pt-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">by {service.provider?.name}</div>
            <div className="font-heading text-lg font-extrabold">
              ₹{service.price}
              {service.pricing_unit && service.pricing_unit !== "flat" && (
                <span className="ml-1 text-xs font-semibold text-muted-foreground">{unitSuffix(service.pricing_unit)}</span>
              )}
            </div>
          </div>
          <span className="rounded-full bg-brand-ink px-3 py-1.5 text-xs font-semibold text-white">Book</span>
        </div>
      </div>
    </Link>
  );
}
